// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Help center + FAQ  (content for pages/HelpCenter.tsx and pages/Faq.tsx)
// Customer-facing help for the ordering app: quick solutions (each links to the screen that really does it) and FAQs. Every answer
// describes something the system actually does today, or cites the published Terms / Privacy Policy by clause — no cold-chain,
// lab-certificate, temperature-sensor, sourcing-partner, eco-box, instant-refund or response-time claims (there is no data or process
// behind them). Change an answer when the behaviour or the documents change. Delivery fee comes from data/delivery.ts; the complaint
// window is the API's `windowHours` (24 — backend domain/claim.py); delivery days/hours are Terms §9.3; refund timing Terms §4.2.
// Neighbours: data/legalContact.ts + data/whatsappContext.ts (the real contact channels), pages/Contact (registered office + form).
// ─────────────────────────────────────────────────────────────────────────────

export interface QuickSolution {
  id: string;
  title: string;
  description: string;
  to: string;
}

export const QUICK_SOLUTIONS: QuickSolution[] = [
  { id: "track", title: "Track an order", description: "See where an order is, its status timeline and invoice", to: "/orders" },
  { id: "complaint", title: "Raise a complaint", description: "Cut, freshness, packaging or delivery — for a delivered order", to: "/complaint" },
  { id: "refunds", title: "Cancellations, refunds & credit", description: "Cancel before confirmation, see your reports and wallet credit", to: "/refund-assistance" },
  { id: "wallet", title: "Your wallet", description: "ZenFresh credits and your transaction history", to: "/wallet" },
  { id: "addresses", title: "Delivery addresses", description: "Add, edit or change your default address", to: "/account/addresses" },
  { id: "faq", title: "All questions & answers", description: "Browse the FAQ by topic", to: "/faq" },
  { id: "policies", title: "Terms & policies", description: "Cancellations, refunds, wallet and grievance contacts", to: "/policies" },
  { id: "certs", title: "Compliance & food safety", description: "Certifications and food-safety information", to: "/compliance" },
];

export type FaqCategory = "orders" | "cuts" | "delivery" | "refunds" | "account";

export const FAQ_CATEGORIES: { id: FaqCategory; label: string }[] = [
  { id: "orders", label: "Orders" },
  { id: "cuts", label: "Cuts" },
  { id: "delivery", label: "Delivery" },
  { id: "refunds", label: "Refunds & wallet" },
  { id: "account", label: "Account & privacy" },
];

export interface HelpFaq {
  q: string;
  a: string;
  category: FaqCategory;
  /** Also shown on the Help center (the FAQ page shows every entry). */
  help?: boolean;
  /** Extra detail the FAQ page renders under the answer. */
  extra?: "cuts";
}

export const HELP_FAQS: HelpFaq[] = [
  {
    category: "orders",
    help: true,
    q: "How do I cancel an order?",
    a: "You can cancel from the order page until ZenFresh confirms the order. The money goes back to the payment method you used, or to your wallet — you choose when you cancel. Once an order is confirmed and being prepared it generally can't be cancelled (Terms §9.7); write to us instead.",
  },
  {
    category: "orders",
    q: "What is the Daily Catch?",
    a: "A page of the day's landings that ZenFresh publishes, with how many packs are left. It refreshes on its own while you have it open, so you can order what is actually available.",
  },
  {
    category: "orders",
    q: "What if an item can't be supplied?",
    a: "If something you ordered isn't available from the day's fresh catch, you'll be told and can substitute it, wait for the next delivery, or cancel that part of the order for a full refund, credited within 4 business days to the original payment method (Terms §4.2).",
  },
  {
    category: "cuts",
    help: true,
    q: "Can I choose how my fish is cut?",
    a: "Yes. Each catch offers its own cuts — pick one when you add it to your cart or on its product page. Some cuts carry a small preparation fee; it is shown before you add.",
    extra: "cuts",
  },
  {
    category: "cuts",
    q: "Can I save the cut and pack size I like?",
    a: "Yes. Tap the heart on a catch to save it to your Favourites with the cut and pack you picked. \"Add to cart\" on the Favourites page uses them, and changing either is remembered for next time.",
  },
  {
    category: "delivery",
    help: true,
    q: "How much is delivery?",
    a: "Delivery is charged per order. The fee, and any order value above which delivery is free, is shown in your cart and at checkout — with your total, including any coupon that applies — before you pay. It can differ by delivery area.",
  },
  {
    category: "delivery",
    q: "Where do you deliver?",
    a: "The Locations page lists the coastal cities we serve and has a pincode check. When you add a delivery address the map tells you straight away whether it is in a served area — you can still save an address that isn't yet.",
  },
  {
    category: "delivery",
    q: "When do orders arrive?",
    a: "Under our Terms (§9.3), orders are delivered between 10:00 a.m. and 1:00 p.m. on service days — Wednesday, Saturday and Sunday — or as shown for your location. These windows are indicative and can vary with traffic, weather or operations.",
  },
  {
    category: "refunds",
    help: true,
    q: "What if something is wrong with my delivery?",
    a: "Raise a complaint from your profile: pick the items affected and what went wrong. The app accepts complaints for 24 hours after delivery (the Terms, §9.8, ask you to contact us within 2 hours where you can, with photos). A person at ZenFresh reviews it; if it's approved, the amount is credited to your wallet.",
  },
  {
    category: "refunds",
    help: true,
    q: "How does my wallet work?",
    a: "Your wallet holds ZenFresh credits: refunds you choose to receive as credit, credit from approved complaints, and rewards ZenFresh may offer. You spend it at checkout. There's no top-up and no cash withdrawal, and credits stay with your account.",
  },
  {
    category: "refunds",
    q: "Where does a cancelled order's refund go?",
    a: "When you cancel you choose: back to the original payment method, or to your wallet. Any part you paid from your wallet goes back to the wallet. The Refund assistance page shows where each cancelled order's money went.",
  },
  {
    category: "account",
    help: true,
    q: "How do I refer a friend?",
    a: "Open Share & Earn on your profile, share your code or invite link, and you can see who joined and how far they've got. Whether a referral earns a reward is shown on that page.",
  },
  {
    category: "account",
    help: true,
    q: "Can I change my mobile number?",
    a: "Your number is what you log in with (an SMS code), so it can't be edited. To use another number, log in with it. You can change your name, nickname, email and photo under Edit profile.",
  },
  {
    category: "account",
    help: true,
    q: "How do I download or delete my data?",
    a: "Open Privacy & data on your profile. You can download your data as a file straight away, or ask ZenFresh to erase your personal data — a person reviews that request, and order and payment records may have to be kept for tax and consumer-protection law (Privacy Policy §8, §14).",
  },
];
